import { Injectable } from '@angular/core';
import { Currency, CurrencyType } from '../models/currency';
import { Observable, throwError, of, forkJoin  } from 'rxjs';
import { map, switchMap, shareReplay, catchError } from 'rxjs/operators';
import { ApiService } from './api.service';
import {keyBy as _keyBy, sortBy as _sortBy} from 'lodash-es';
import { AuthService, AuthStatus } from './auth.service';
import { MetadataService } from './metadata.service';
import { ItemsList } from '@ng-select/ng-select/lib/items-list';


@Injectable({
  providedIn: 'root'
})
export class CurrenciesService {

  request: Observable<Currency[]>;

  constructor(private api: ApiService, private auth: AuthService, private metadata: MetadataService) {
    this.auth.statusChanges.subscribe(status => {
      if (status === AuthStatus.LoggedIn || status === AuthStatus.LoggedOff) {
        this.request = null;
      }
    });
  }

  getAll(): Observable<Currency[]> {
    if (!this.request) {
      this.request = this.api.get(`{
          currencies {
            code
            name
            type
            currentExchangeRate {
              amount
              currency
            }
            currentUsdExchangeRate {
              amount
              currency
            }
            allowedDecimals
            imageUrl
            colorCode
            sortOrder
            acceptedPairs
            allStablePairs
            categories {
              id
              name
            }
          }
        }`, null, this.auth.getToken() ? true : false).pipe(
        switchMap<any, Observable<Currency[]>>(response => {
          const data = response['data']['currencies'];
          if (data && data['errors']) {
            return throwError(data['errors']);
          } else if (data) {
            return of(data);
          }

          return throwError(response);
        }),
        this.api.unifyErrors(),
        catchError(err => {
          this.request = null;
          return throwError(err);
        }),
        shareReplay()
      );
    }

    return this.request;
  }

  getAllSorted(): Observable<Currency[]> {
    return this.getAll().pipe(
      map((currencies) => {
        if (currencies) {
          currencies.sort((c1, c2) => {
            return c1.sortOrder < c2.sortOrder ? -1 : 1;
          });
        }

        return currencies;
      })
    );
  }

  getAllHash(): Observable<{[key: string]: Currency}> {
    return this.getAllSorted().pipe(
      map(currencies => {
       return  _keyBy<Currency>(currencies, (val) => val.code);
      })
    );
  }

  getOne(code: string): Observable<Currency> {
    return this.getAllSorted().pipe(
      map(currencies => {
       return  currencies.find(item => item.code === code);
      })
    );
  }

  getFiltered(filter: (currency: Currency) => boolean): Observable<Currency[]> {
    return this.getAllSorted().pipe(
      map((currencies) => {
        if (currencies) {
          currencies = currencies.filter((currency: Currency) => {
            return filter(currency);
          });
        }

        return currencies;
      })
    );
  }

  getHashFiltered(filter: (currency: Currency) => boolean): Observable<{[key: string]: Currency}> {
    return this.getFiltered(filter).pipe(
      map(currencies => {
       return  _keyBy<Currency>(currencies, (val) => val.code);
      })
    );
  }

  getByType(type: CurrencyType, enabledOnly = false): Observable<Currency[]> {
    return this.getFiltered(currency => currency.type === type && (!enabledOnly || currency.acceptedPairs != null));
  }

  getHashByType(type: CurrencyType): Observable<{[key: string]: Currency}> {
    return this.getHashFiltered(currency => currency.type === type);
  }

  getFiat(): Observable<Currency[]> {
    return this.getByType(CurrencyType.Fiat);
  }

  getCrypto(): Observable<Currency[]> {
    return this.getByType(CurrencyType.Crypto);
  }

  getCryptoWithSupportedBase(baseCurrency: string): Observable<Currency[]> {
    return this.getFiltered(currency => currency.type === CurrencyType.Crypto && currency.allStablePairs && currency.allStablePairs.includes(`${currency.code}-${baseCurrency}`));

  }

  getFiatHash(): Observable<{[key: string]: Currency}> {
    return this.getHashByType(CurrencyType.Fiat);
  }

  getCryptoHash(): Observable<{[key: string]: Currency}> {
    return this.getHashByType(CurrencyType.Crypto);
  }

  getFiatSorted() {
    return forkJoin({
      userCurrency: this.metadata.userCurrency(),
      fiatCurrencies: this.getFiat()
    }).pipe(
      map(response => {
        return _sortBy(response.fiatCurrencies, [
          item => item.code === response.userCurrency ? 0 : 1,
          item =>item.sortOrder
        ]);
      })
    );
  }

  getFiatFiltered(filter: (currency: Currency) => boolean): Observable<Currency[]> {
    return this.getFiat().pipe(
      map (currencies => {
        if (currencies) {
          currencies = currencies.filter((currency: Currency) => {
            return filter(currency);
          });
        }

        return currencies;
      })
    )
  }

  getFiatWithoutUsd() {
    return this.getFiatFiltered(item => item.code !== 'USD');
  }

  clearCache() {
    this.request = null;
  }

  getCurrencyPrecision(currency: string): number {
    switch (!!currency) {
      case ['COMP', 'SOL', 'EGLD'].includes(currency):
        return 2;
      case [ 'ETHW', 'ETC', 'EOS', 'OMG', 'USDT', 'NEO', 'XTZ', 'LINK', 'KNC', 'UNI', 'BAL', 'USDC', 'WAVES', 'DOT', 'NEAR', 'ATOM', 'CRV', 'FIL', 'FTT', 'LEO', 'QTUM', 'LUNA2'].includes(currency):
        return 3;
      case [ 'XRP', 'XLM', 'ZRX', 'BAT', 'ALGO', 'DAI', 'BNT', 'ENJ', 'LRC', 'MANA', 'MATIC', 'STORJ', 'SUSHI', 'ADA', '1INCH', 'ANT', 'BAND', 'IOTA', 'SNX', 'THETA'].includes(currency):
        return 5;
      case [ 'DOGE', 'FTM', 'CHZ', 'GALA', 'GRT', 'OCEAN', 'TRX'].includes(currency):
        return 6;
      case ['DGB', 'VET', 'XVG'].includes(currency):
        return 7;
      case ['SHIB'].includes(currency):
        return 10;
      default:
        return 2;
    }
  }
}
